import { FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
// Components
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Delete,
  Download,
  Clear,
  Check,
  Visibility,
  Update,
  Send
} from '@mui/icons-material';
import { resolveObjectField, truncString } from '../../helpers';

interface IMobileTable {
  original: any;
  handleEdit?: (val: any) => void;
  handleDelete?: (val: any) => void;
  handleDownload?: (val: any) => void;
  handleUpdate?: (val: any) => void;
  handleApproval?: (val: any) => void;
  handleAccordionLabel?: (val: any) => void;
  handleView?: (val: any) => void;
  sendReminder?: (val: any) => void;
  fields: {
    name: string;
    accessor: string;
    Cell?: (value: { cell: { row: { original: Record<string, unknown> } } }) => JSX.Element;
  }[];
  mobileDefaultAccessor?: string;
  mobileSummaryAccessor?: string;
  useFirstCell?: boolean;
  useMobileDefaultAccessorCell?: boolean;
  includeDeleteInDetails?: boolean;
  getAccordionHeader?: (val: any) => string;
  showEdit?: (val: any) => boolean;
  renderActions?: (val: any) => JSX.Element;
}

export const MobileTable: FC<IMobileTable> = ({
  handleDownload,
  handleApproval,
  handleEdit,
  handleDelete,
  handleView,
  handleUpdate,
  fields,
  original,
  mobileSummaryAccessor,
  mobileDefaultAccessor,
  useFirstCell,
  useMobileDefaultAccessorCell,
  // allows delete button to be used in body of accordion if true & hides it from summary
  includeDeleteInDetails,
  // allows for custom header to be passed in. Passes the original object to the function
  getAccordionHeader,
  showEdit,
  sendReminder,
  renderActions
}) => {
  const classes = useStyles({ includeDeleteInDetails });

  // Fetch field data based on mobileSummaryAccessor for the summary
  const summaryFieldData = mobileSummaryAccessor
    ? truncString(resolveObjectField(mobileSummaryAccessor, original), 30)
    : null;

  // Fetch field data based on mobileDefaultAccessor for the details
  const customField =
    mobileDefaultAccessor && fields.find(val => val.accessor === mobileDefaultAccessor);

  // for the accordion header and the correct props are passed, find the custom cell funtion based on the custom field key name
  const customCellAccessor =
    useMobileDefaultAccessorCell &&
    mobileDefaultAccessor &&
    !useFirstCell &&
    customField?.Cell({ cell: { row: { original } } });

  // for the accordion header and no props are passed so find the accessor value for for first field
  const firstAccessorDefault =
    !useFirstCell &&
    !mobileDefaultAccessor &&
    !useMobileDefaultAccessorCell &&
    original[fields[0].accessor];

  // for the accordion header and useFirstCell is pass, look at the first field cell function and render that
  const firstCellDefault =
    useFirstCell &&
    !mobileDefaultAccessor &&
    !useMobileDefaultAccessorCell &&
    fields?.[0]?.Cell({ cell: { row: { original } } });

  // for the accordion header and mobileDefaultAccessor is passed, look at the field that matches that
  const customAccessor =
    !useMobileDefaultAccessorCell &&
    !useFirstCell &&
    mobileDefaultAccessor &&
    original[mobileDefaultAccessor];

  // reusable delete button
  const deleteButton = handleDelete ? (
    <IconButton
      className={classes.deleteButton}
      color="default"
      onClick={e => {
        e.stopPropagation();
        handleDelete(original);
      }}
      size="large"
    >
      <Delete />
    </IconButton>
  ) : null;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">
            {getAccordionHeader?.(original) ||
              summaryFieldData ||
              customCellAccessor ||
              customAccessor ||
              firstCellDefault ||
              firstAccessorDefault}
          </Typography>
          <div className={classes.buttonsWrapper}>
            {/* If renderActions is provided, use it instead of default buttons */}
            {renderActions ? (
              renderActions(original)
            ) : (
              <>
                {handleDownload && (
                  <IconButton
                    className={classes.editButton}
                    color="primary"
                    onClick={e => {
                      e.stopPropagation();
                      handleDownload(original);
                    }}
                    size="large"
                  >
                    <Download />
                  </IconButton>
                )}
                {handleEdit && (showEdit !== undefined ? showEdit?.(original) : true) && (
                  <IconButton
                    className={classes.editButton}
                    color="primary"
                    onClick={e => {
                      e.stopPropagation();
                      handleEdit(original);
                    }}
                    size="large"
                  >
                    <Edit />
                  </IconButton>
                )}
                {handleUpdate && (
                  <IconButton
                    className={classes.viewButton}
                    color="primary"
                    onClick={e => {
                      e.stopPropagation();
                      handleUpdate(original);
                    }}
                    size="large"
                  >
                    <Update />
                  </IconButton>
                )}
                {!includeDeleteInDetails && deleteButton}
                {handleApproval && (
                  <IconButton
                    className={
                      original.paymentStatus === 'Approved'
                        ? classes.rejectButton
                        : classes.approvalButton
                    }
                    color="default"
                    onClick={e => {
                      e.stopPropagation();
                      handleApproval(original);
                    }}
                    size="large"
                  >
                    {original.paymentStatus === 'Approved' ? <Clear /> : <Check />}
                  </IconButton>
                )}
                {handleView && (
                  <IconButton
                    className={classes.viewButton}
                    color="primary"
                    onClick={e => {
                      e.stopPropagation();
                      handleView(original);
                    }}
                    size="large"
                  >
                    <Visibility />
                  </IconButton>
                )}
                {sendReminder && (
                  <IconButton
                    className={classes.viewButton}
                    color="primary"
                    onClick={e => {
                      e.stopPropagation();
                      sendReminder(original);
                    }}
                    size="large"
                  >
                    <Send />
                  </IconButton>
                )}
              </>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        {fields.map((field: (typeof fields)[0], index) => {
          if (field.accessor === 'punchListItemDescription') {
            return (
              <div key={`${index}`} className={classes.panelSummaryWrapper}>
                <FormLabel
                  component="span"
                  className={clsx(classes.labelColor, classes.boldName, classes.subLabel)}
                >
                  {field?.name?.length > 1 ? `${field.name}:` : field.name}
                </FormLabel>
                <Typography component="span" variant="body2" style={{ paddingTop: '4px' }}>
                  {original.description}
                </Typography>
              </div>
            );
          }
          return (
            <div key={`${index}`} className={classes.panelSummaryWrapper}>
              <FormLabel
                component="span"
                className={clsx(classes.labelColor, classes.boldName, classes.subLabel)}
              >
                {field?.name?.length > 1 ? `${field.name}:` : field.name}
              </FormLabel>
              <Typography component="span" variant="body2" style={{ paddingTop: '4px' }}>
                {typeof field.accessor === 'string' && typeof field.Cell !== 'function'
                  ? !resolveObjectField(field.accessor, original)
                    ? '--'
                    : resolveObjectField(field.accessor, original)
                  : typeof field.Cell === 'function'
                  ? ''
                  : '--'}
                {typeof field.Cell === 'function'
                  ? field.Cell({ cell: { row: { original } } })
                  : null}
              </Typography>
            </div>
          );
        })}
        {includeDeleteInDetails && deleteButton}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    display: 'flex',
    padding: '0.25rem 0',
    '&:first-child': { marginTop: 0 }
  },
  details: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '1.125rem 1rem',
    backgroundColor: theme.palette.grey[100]
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: (props: { includeDeleteInDetails?: boolean }) => ({
    padding: `2px 5px`,
    color: theme.palette.error.main,
    // if delete is in accordion summary position top right
    ...(props.includeDeleteInDetails && {
      position: 'absolute',
      right: theme.spacing(0.5)
    })
  }),
  approvalButton: {
    padding: `2px`,
    marginRight: '6px',
    backgroundColor: theme.palette.success.main,
    color: '#ffffff',
    '&:hover': {
      backgroundColor: theme.palette.success.main,
      color: '#ffffff'
    },
    '& svg': {
      width: '.75em',
      height: '.75em'
    }
  },
  rejectButton: {
    padding: `2px`,
    marginRight: '6px',
    backgroundColor: theme.palette.error.main,
    color: '#ffffff',
    '&:hover': { backgroundColor: theme.palette.error.main, color: '#ffffff' },
    '& svg': {
      width: '.75em',
      height: '.75em'
    }
  },
  viewButton: {
    padding: `2px 5px`
  },
  boldName: {
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  labelColor: {
    color: theme.palette.typography.labels
  }
}));
